/*  ========================================================================
    # Components - Lazy Image
    ========================================================================  */

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';
import useLocalization from '../hooks/useLocalization';
import LazySource from './LazySource';

const LazyImage = ({
  figureClass,
  imageClass,
  children,
  defaultUrl,
  placeholderUrl,
  alt,
  caption,
  credits,
  onClick,
  focusY,
  focusX,
  preload,
  hasEnlarge = false
}) => {
  const imageElmRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Icons & Placeholder
  const local = useLocalization();
  const iconEnlarge = `${local('svgSprite')}#zoom`;
  const defaultPlaceholder = placeholderUrl ? placeholderUrl : 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mPs+Q8AAh0BjeU/5JAAAAAASUVORK5CYII=';
  const figureStyle = {
    'backgroundImage': !imageLoaded && `url(${defaultPlaceholder})`,
    '--y-focus': focusY,
    '--x-focus': focusX
  };

  const sourceElements = (items, isVisible) => {
    if (!items) {
      return null;
    } else {
      return Array.isArray(items) ? items.map((child, index) =>
        React.cloneElement(child, { key: index, visible: preload || isVisible })
      ) : React.cloneElement(items, { visible: preload || isVisible });
    }
  };

  const { ref, inView } = useInView({
    rootMargin: '300px',
    threshold: 0,
    triggerOnce: true
  });

  useEffect(() => {
    imageElmRef.current?.addEventListener('load', setImageLoaded(true));
    return () => imageElmRef.current?.removeEventListener('load', setImageLoaded(true));
  }, [imageElmRef.current]);

  return (
    <figure
      ref={ref}
      onClick={onClick}
      style={figureStyle}
      className={[
        'lazy-image',
        (inView && imageLoaded) || preload ? '' : 'lazy-image--pending',
        !inView && !imageLoaded && !placeholderUrl ? 'lazy-image--loading' : '',
        figureClass
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className='lazy-image__loader' />
      <picture>
        {sourceElements(children, inView)}
        <img
          className={['lazy-image__img', imageClass].filter(Boolean).join(' ')}
          src={(inView && imageLoaded) || preload ? defaultUrl : defaultPlaceholder}
          alt={alt}
          ref={imageElmRef}
        />
      </picture>
      {(caption && caption !== '') || (credits && credits !== '') ? (
        <figcaption>
          {caption && caption !== '' && credits === '' ? caption : ''}
          {caption && caption !== '' && credits && credits !== '' ? `${caption} - © ${credits}` : ''}

          {credits && credits !== '' && !caption ? <span>©</span> : null}
          {credits && credits !== '' && !caption ? ` ${credits}` : ''}
        </figcaption>
      ) : null}
      {hasEnlarge && (
        <div className='c-expandable-image__enlarge'>
          <svg viewBox='0 0 200 200'>
            <use href={iconEnlarge}></use>
          </svg>
        </div>
      )}
    </figure>
  );
};

LazyImage.propTypes = {
  figureClass: PropTypes.string,
  imageClass: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.objectOf(LazySource), PropTypes.arrayOf(LazySource)]),
  defaultUrl: PropTypes.string,
  placeholderUrl: PropTypes.string,
  alt: PropTypes.string,
  caption: PropTypes.string,
  credits: PropTypes.string,
  onClick: PropTypes.func,
  focusY: PropTypes.string,
  focusX: PropTypes.string,
  preload: PropTypes.bool,
  hasEnlarge: PropTypes.bool
};

export default LazyImage;
